import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import {
  AttributesEditor,
  type AttributeRow,
} from '@/features/catalogs/AttributesEditor';
import type { CreateProductInput, Product, ProductStatus } from '@/lib/api/types';

interface ProductFormProps {
  initial?: Product;
  onSubmit: (input: CreateProductInput) => void;
  onCancel: () => void;
  isPending: boolean;
  error?: Error | null;
}

/** Create/edit form for a product, including the dynamic attributes editor. */
export function ProductForm({ initial, onSubmit, onCancel, isPending, error }: ProductFormProps) {
  const [sku, setSku] = useState(initial?.sku ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [brand, setBrand] = useState(initial?.brand ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [price, setPrice] = useState(initial?.price ?? '');
  const [currency, setCurrency] = useState(initial?.currency ?? '');
  const [status, setStatus] = useState<ProductStatus>(initial?.status ?? 'active');
  const [attributes, setAttributes] = useState<AttributeRow[]>(
    initial
      ? Object.entries(initial.attributes).map(([key, value]) => ({ key, value: String(value) }))
      : [],
  );

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const attributesMap: Record<string, string> = {};
    for (const row of attributes) {
      if (row.key) attributesMap[row.key] = row.value;
    }
    onSubmit({
      sku,
      name,
      description: description || null,
      brand: brand || null,
      category: category || null,
      price: price || null,
      currency: price ? currency || null : null,
      status,
      attributes: attributesMap,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          required
          data-testid="product-sku-input"
        />
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          data-testid="product-name-input"
        />
      </div>
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        data-testid="product-description-input"
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Brand"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          data-testid="product-brand-input"
        />
        <Input
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="product-category-input"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Input
          label="Price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="9.99"
          data-testid="product-price-input"
        />
        <Input
          label="Currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          placeholder="USD"
          maxLength={3}
          data-testid="product-currency-input"
        />
        <Select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as ProductStatus)}
          data-testid="product-status-select"
        >
          <option value="active">Active</option>
          <option value="discontinued">Discontinued</option>
        </Select>
      </div>

      <AttributesEditor rows={attributes} onChange={setAttributes} />

      {error && (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error.message}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={isPending} data-testid="save-product-button">
          {isPending ? 'Saving...' : initial ? 'Save changes' : 'Add product'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          data-testid="cancel-product-button"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
